import { useMemo, type ComponentType } from "react";

import { axiosGet } from "@mobsuccess-devops/react-shared/fetch";
import {
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";

type NavbarUser = {
  type: "admin" | "headquarters" | "group";
  admin: boolean;
  rights?: object;
  user_id: string;
  user_email: string;
  admin_type: string;
  user_lastname: string;
  user_firstname: string;
  admin_firstname?: string;
  admin_lastname?: string;
  admin_email?: string;
};

export type Navbar = {
  account: {
    account_id: number;
    account_name: string;
    publisher_currency: "USD" | "EUR";
  };
  client: {
    client_id: string;
    client_label: string;
  };
  user: NavbarUser;
  footerMenu: {
    logoutUrl: string;
    adminUrl: string;
    pages: Array<{
      url: string;
      label: string;
      key?: string;
      Icon?: ComponentType;
      iconSrc?: string;
    }>;
  };
  sections: Array<{
    current: boolean;
    iconSrc?: string;
    Icon?: ComponentType;
    label: string;
    pageUrl: string;
    entrypoint: string;
  }>;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- this is an interface augmentation
  interface Window {
    _msNavbarData?: Navbar;
  }
}

async function getNavbar(): Promise<Navbar> {
  const { data } = await axiosGet<Navbar>({
    entity: "dashboardNavbar",
    verb: "get",
    params: {
      version: 2,
    },
  });
  window._msNavbarData = data;
  return {
    ...data,
    account: {
      ...data.account,
      account_id: +data.account.account_id,
    },
  };
}

const navbarQueryKey = ["platform/sidebar-config"];

export function getNavbarQueryOptions(): UseQueryOptions<Navbar> {
  return {
    queryKey: navbarQueryKey,
    queryFn: getNavbar,
  };
}

export async function fetchNavbar(queryClient: QueryClient): Promise<Navbar> {
  return await queryClient.fetchQuery(getNavbarQueryOptions());
}

export async function prefetchNavbar(queryClient: QueryClient): Promise<void> {
  await queryClient.prefetchQuery(getNavbarQueryOptions());
}

export function getNavbarQueryData(
  queryClient: QueryClient,
): Navbar | undefined {
  return queryClient.getQueryData(navbarQueryKey);
}

export function useUnsafeQueryNavbar(): UseQueryResult<Navbar> {
  return useQuery(getNavbarQueryOptions());
}

export function useQueryNavbar(): Extract<
  UseQueryResult<Navbar>,
  { isSuccess: true }
> {
  const query = useUnsafeQueryNavbar();
  if (!query.isSuccess) {
    throw new Error(
      "You should not call this hook outside the proper wrappers (see react-shared EnsureUser)",
    );
  }
  return query;
}

export function getRights(obj: object, prefix: string[] = []): Set<string> {
  const keys = new Set<string>();
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "object") {
      keys.add([...prefix, key].join("."));
      const subright = getRights(value, [...prefix, key]);
      for (const right of subright) {
        keys.add(right);
      }
    } else {
      if (value) {
        keys.add([...prefix, key].join("."));
      }
    }
  }
  return keys;
}

/**
 * @deprecated Use `useWorkspace` instead.
 */
export function useLoggedInUser(): Omit<NavbarUser, "rights"> & {
  rights: Set<string>;
} {
  const { data: navbar } = useQueryNavbar();
  return useMemo(() => {
    const rights = getRights(navbar.user.rights ?? {});
    return {
      ...navbar.user,
      rights,
    };
  }, [navbar]);
}

export function useIsAdmin(): boolean {
  const user = useLoggedInUser();
  return !!user.admin;
}
